import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  House,
  Key,
  Printer,
  IdentificationCard,
  Books,
  Desktop,
  Users as UsersIcon,
  Receipt,
  SignOut,
  List,
  X,
  ArrowsClockwise,
  ClipboardText,
  Gear,
  Money,
  Wrench,
  ArrowsCounterClockwise,
  ChartLineUp,
  Calculator,
  Toolbox,
  Package,
  MapPin,
  WarningOctagon,
  Truck,
  Storefront,
  ChartBar,
  CaretDown,
  CaretRight,
} from '@phosphor-icons/react';

// Collapsible groups. Each is rendered at a specific anchor (insert_after path).
const GROUPS = [
  {
    id: 'catalog',
    storageKey: 'sidebar-catalog-open',
    insertAfter: '/',
    label: 'Création produits et prestations',
    icon: Storefront,
    roles: ['admin', 'seller'],
    children: [
      { path: '/keys', label: 'Clés', icon: Key, roles: ['admin', 'seller'] },
      { path: '/printing', label: 'Impressions et objets associés', icon: Printer, roles: ['admin', 'seller'] },
      { path: '/business-cards', label: 'Cartes de visite', icon: IdentificationCard, roles: ['admin', 'seller'] },
      { path: '/binding', label: 'Reliure et Plastification', icon: Books, roles: ['admin', 'seller'] },
      { path: '/it-products', label: 'Produits informatiques et électroniques', icon: Desktop, roles: ['admin', 'seller'] },
      { path: '/prestations', label: 'Prestations de service', icon: Toolbox, roles: ['admin', 'seller'] },
      { path: '/services', label: 'Interventions à domicile', icon: Wrench, roles: ['admin', 'seller'] },
    ],
  },
  {
    id: 'accounting',
    storageKey: 'sidebar-accounting-open',
    insertAfter: '/cash-register',
    label: 'Comptabilité et Rapports',
    icon: ChartBar,
    roles: ['admin', 'seller'],
    children: [
      { path: '/stock-movements', label: 'Mouvements stock', icon: ArrowsClockwise, roles: ['admin', 'seller'] },
      { path: '/pertes', label: 'Pertes / rebut', icon: WarningOctagon, roles: ['admin', 'seller'] },
      { path: '/reports/services', label: 'Rapport interventions', icon: ChartLineUp, roles: ['admin'] },
      { path: '/accounting/exports', label: 'Exports comptables', icon: Calculator, roles: ['admin'] },
    ],
  },
];

// Paths that the groups manage (so we don't render them again as flat items)
const GROUPED_PATHS = new Set(GROUPS.flatMap((g) => g.children.map((c) => c.path)));

// Flat items shown in the sidebar (ordered)
const MENU_ITEMS = [
  { path: '/', label: 'Tableau de bord', icon: House, roles: ['admin', 'seller'] },
  // catalog group inserted here
  { path: '/keys', label: 'Clés', icon: Key, roles: ['admin', 'seller'] },
  { path: '/printing', label: 'Impressions et objets associés', icon: Printer, roles: ['admin', 'seller'] },
  { path: '/business-cards', label: 'Cartes de visite', icon: IdentificationCard, roles: ['admin', 'seller'] },
  { path: '/binding', label: 'Reliure et Plastification', icon: Books, roles: ['admin', 'seller'] },
  { path: '/it-products', label: 'Produits informatiques et électroniques', icon: Desktop, roles: ['admin', 'seller'] },
  { path: '/prestations', label: 'Prestations de service', icon: Toolbox, roles: ['admin', 'seller'] },
  { path: '/services', label: 'Interventions à domicile', icon: Wrench, roles: ['admin', 'seller'] },
  { path: '/interventions', label: 'Gestion des interventions', icon: Toolbox, roles: ['admin', 'seller', 'technician'] },
  { path: '/stock-view', label: 'Stock marchandises', icon: Package, roles: ['technician'] },
  { path: '/interventions-map', label: 'Carte interventions', icon: MapPin, roles: ['technician', 'admin'] },
  { path: '/clients', label: 'Clients', icon: UsersIcon, roles: ['admin', 'seller'] },
  { path: '/suppliers', label: 'Fournisseurs', icon: Truck, roles: ['admin', 'seller'] },
  { path: '/quotes', label: 'Devis', icon: ClipboardText, roles: ['admin', 'seller'] },
  { path: '/counter-sales', label: 'Vente Comptoir', icon: Money, roles: ['admin', 'seller'] },
  { path: '/invoices', label: 'Factures', icon: Receipt, roles: ['admin', 'seller'] },
  { path: '/credit-notes', label: 'Avoirs', icon: ArrowsCounterClockwise, roles: ['admin', 'seller'] },
  { path: '/sav', label: 'SAV', icon: Wrench, roles: ['admin', 'seller'] },
  { path: '/cash-register', label: 'Caisse', icon: Money, roles: ['admin', 'seller'] },
  // accounting group inserted here
  { path: '/stock-movements', label: 'Mouvements stock', icon: ArrowsClockwise, roles: ['admin', 'seller'] },
  { path: '/pertes', label: 'Pertes / rebut', icon: WarningOctagon, roles: ['admin', 'seller'] },
  { path: '/reports/services', label: 'Rapport interventions', icon: ChartLineUp, roles: ['admin'] },
  { path: '/accounting/exports', label: 'Exports comptables', icon: Calculator, roles: ['admin'] },
  { path: '/users', label: 'Utilisateurs', icon: List, roles: ['admin'] },
  { path: '/settings', label: 'Paramètres', icon: Gear, roles: ['admin'] },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const role = user?.role;

  // For each group: derived state (childrenFiltered, isActive) + open/setOpen state
  const groupRuntime = GROUPS.map((g) => {
    const children = g.children.filter((c) => c.roles.includes(role));
    const childPaths = children.map((c) => c.path);
    const isActive = childPaths.includes(location.pathname);
    const visible = g.roles.includes(role) && children.length > 0;
    return { group: g, children, isActive, visible };
  });

  const [openMap, setOpenMap] = useState(() => {
    const initial = {};
    groupRuntime.forEach(({ group, isActive }) => {
      try {
        const stored = localStorage.getItem(group.storageKey);
        initial[group.id] = stored !== null ? stored === '1' : isActive;
      } catch (e) {
        initial[group.id] = isActive;
      }
    });
    return initial;
  });

  const toggleGroup = (group) => {
    setOpenMap((prev) => {
      const next = !prev[group.id];
      try { localStorage.setItem(group.storageKey, next ? '1' : '0'); } catch (e) { /* ignore */ }
      return { ...prev, [group.id]: next };
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Mobile: sidebar closed by default, toggled by hamburger, auto-close on route change
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Preserve sidebar scroll position across route changes (Sidebar re-mounts on every page).
  const navRef = useRef(null);
  const SCROLL_KEY = 'sidebar-nav-scroll';
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    let saved = 0;
    try {
      saved = parseInt(sessionStorage.getItem(SCROLL_KEY) || '0', 10) || 0;
    } catch (e) { /* ignore */ }

    // Restore attempt: may be clamped to 0 if content hasn't laid out yet
    // (e.g. logo image not yet loaded → nav has no overflow yet).
    // Re-try after logo loads AND via ResizeObserver until scrollTop sticks.
    const applyRestore = () => {
      if (!navRef.current) return;
      const target = saved;
      navRef.current.scrollTop = target;
    };

    applyRestore();
    // Retry after layout stabilises
    requestAnimationFrame(() => requestAnimationFrame(applyRestore));

    // Retry when nav's scrollable area grows (e.g. logo image finishes loading)
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      let attempts = 0;
      ro = new ResizeObserver(() => {
        if (attempts++ > 10) { ro.disconnect(); return; }
        if (navRef.current && navRef.current.scrollTop !== saved && navRef.current.scrollHeight > navRef.current.clientHeight) {
          navRef.current.scrollTop = saved;
        }
      });
      ro.observe(el);
    }

    const onScroll = () => {
      try { sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop)); } catch (e) { /* ignore */ }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (ro) ro.disconnect();
    };
  }, []);

  const renderNavLink = (item, indent = false) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.path.slice(1) || 'home'}`}
        onClick={() => setMobileOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors duration-200 ${
          isActive
            ? 'bg-blue-50 text-blue-700 font-medium'
            : 'text-slate-700 hover:bg-slate-50'
        } ${indent ? 'ml-4 pl-2 border-l border-slate-200' : ''}`}
      >
        <Icon size={indent ? 18 : 20} weight={isActive ? 'fill' : 'regular'} />
        <span className="text-sm">{item.label}</span>
      </Link>
    );
  };

  const renderGroup = (runtime) => {
    const { group, children, isActive } = runtime;
    const open = !!openMap[group.id];
    const GroupIcon = group.icon;
    const CaretIcon = open ? CaretDown : CaretRight;
    return (
      <div key={`group-${group.id}`}>
        <button
          type="button"
          onClick={() => toggleGroup(group)}
          data-testid={`nav-group-${group.id}`}
          aria-expanded={open}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors duration-200 ${
            isActive
              ? 'bg-blue-50 text-blue-700 font-medium'
              : 'text-slate-700 hover:bg-slate-50'
          }`}
        >
          <GroupIcon size={20} weight={isActive ? 'fill' : 'regular'} />
          <span className="text-sm flex-1 text-left">{group.label}</span>
          <CaretIcon size={14} weight="bold" />
        </button>
        {open && (
          <div data-testid={`group-${group.id}-children`} className="mb-1">
            {children.map((child) => renderNavLink(child, true))}
          </div>
        )}
      </div>
    );
  };

  // Build the visible list, skipping items already inside a group
  const renderItems = [];
  MENU_ITEMS.forEach((item) => {
    if (!item.roles.includes(role)) return;
    if (GROUPED_PATHS.has(item.path)) return;
    renderItems.push(renderNavLink(item));

    // Insert any group whose insertAfter matches this item
    groupRuntime.forEach((runtime) => {
      if (runtime.group.insertAfter === item.path && runtime.visible) {
        renderItems.push(renderGroup(runtime));
      }
    });
  });

  return (
    <>
      {/* Mobile hamburger — only visible on small screens */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        aria-label="Ouvrir le menu"
        data-testid="mobile-menu-btn"
        className="md:hidden fixed top-3 left-3 z-40 inline-flex items-center justify-center w-11 h-11 rounded-md bg-white border border-slate-300 shadow text-slate-700"
      >
        <List size={22} weight="bold" />
      </button>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-menu-backdrop"
        />
      )}

      <div className={`sidebar ${mobileOpen ? 'open' : ''}`} data-testid="sidebar">
        {/* Close button on mobile (top-right of the sidebar) */}
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label="Fermer le menu"
          data-testid="mobile-menu-close-btn"
          className="md:hidden absolute top-2 right-2 inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-500 hover:bg-slate-100"
        >
          <X size={20} weight="bold" />
        </button>

        <div className="p-4 border-b border-slate-100">
          <img
            src="/lbc-logo.png"
            alt="LBC - Votre solution de gestion"
            className="w-full h-auto max-h-36 object-contain mb-2"
            data-testid="app-title"
          />
          <p className="text-sm text-slate-500 mt-2 text-center">{user?.full_name || user?.username}</p>
          <div className="flex justify-center">
            <span className="inline-block mt-2 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
              {role === 'admin' ? 'Administrateur' : role === 'technician' ? 'Technicien' : 'Vendeur'}
            </span>
          </div>
        </div>

        <nav ref={navRef} className="sidebar-nav px-3 py-2">
          {renderItems}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md w-full text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
          >
            <SignOut size={20} />
            <span className="text-sm">Déconnexion</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
